import axios, { type AxiosRequestConfig, type AxiosRequestHeaders } from 'axios';
import httpClient from './httpClient';
import { API_BASE_URL } from './httpClient';
import { startApiRequest, finishApiRequest } from '../state/useApiStatusStore';

export type ApiErrorDetails = Record<string, string[]>;

type ApiEnvelope<T> = {
	success: boolean;
	data: T | null;
	error?: {
		code?: string;
		message: string;
		details?: ApiErrorDetails;
	} | null;
	meta?: Record<string, unknown> | null;
};

export type ApiResult<T> =
	| { ok: true; data: T; meta?: Record<string, unknown> | null }
	| { ok: false; error: string; status?: number; code?: string; details?: ApiErrorDetails };

export interface ApiMessage {
	message: string;
}

export interface RegistrationPhotoClientEventRequestDto {
	eventType: string;
	reasonCode?: string | null;
	message?: string | null;
	mimeType?: string | null;
	fileExtension?: string | null;
	fileSizeBytes?: number | null;
	selectedFileCount?: number | null;
	acceptedFileCount?: number | null;
	uploadCode?: string | null;
	uploadStatus?: number | null;
}

const ensureLeadingSlash = (path: string): string => (path.startsWith('/') ? path : `/${path}`);
const PHOTO_UPLOAD_TIMEOUT_MS = 120_000;
const REGISTRATION_PHOTO_UPLOAD_TIMEOUT_MS = 240_000;
const PHOTO_UPLOAD_WARMUP_TIMEOUT_MS = 170_000;
const REGISTRATION_EMAIL_STEP_TIMEOUT_MS = 60_000;

type UnknownErrorPayload = {
	code?: string;
	message?: string;
	details?: ApiErrorDetails;
};

type ApiErrorMeta = {
	errorId?: string;
	traceId?: string;
};

const getApiErrorReference = (meta?: ApiErrorMeta | null): string | undefined => {
	const reference = meta?.errorId ?? meta?.traceId;
	const normalized = reference?.trim();
	return normalized ? normalized : undefined;
};

const formatApiErrorMessage = (
	message: string,
	status?: number,
	code?: string,
	meta?: ApiErrorMeta | null
): string => {
	const trimmedMessage = message.trim();
	const isGenericStatusMessage = /^request failed with status code \d+$/i.test(trimmedMessage);
	const rateLimitFallback = 'Too many requests. Please wait a moment and try again.';

	let normalizedMessage =
		isGenericStatusMessage && status
			? status === 429
				? rateLimitFallback
				: 'Request failed. Please try again.'
			: trimmedMessage;

	if (code === 'unexpected_error') {
		const reference = getApiErrorReference(meta);
		if (reference) {
			normalizedMessage = `${normalizedMessage} Reference: ${reference}`;
		}
	}

	return normalizedMessage;
};

const parseUnknownErrorPayload = (payload: unknown): UnknownErrorPayload => {
	if (!payload) {
		return {};
	}

	// Typical ASP.NET Core ValidationProblemDetails:
	// { type, title, status, traceId, errors: { Field: ["msg"] } }
	if (typeof payload === 'object') {
		const obj = payload as Record<string, unknown>;

		const code = typeof obj.code === 'string' ? obj.code : undefined;
		const message = typeof obj.message === 'string' ? obj.message : undefined;
		const error = typeof obj.error === 'string' ? obj.error : undefined;
		if (message || error || code) {
			const inferredCode =
				code ??
				(error?.toLowerCase().includes('rate limit') || message?.toLowerCase().includes('too many requests')
					? 'rate_limited'
					: undefined);

			return {
				code: inferredCode,
				message: message ?? error
			};
		}

		const errors = obj.errors;
		if (errors && typeof errors === 'object') {
			const details: ApiErrorDetails = {};
			for (const [key, value] of Object.entries(errors as Record<string, unknown>)) {
				if (Array.isArray(value)) {
					details[key] = value.map((item) => String(item));
				} else if (typeof value === 'string') {
					details[key] = [value];
				}
			}

			const first = Object.values(details)[0]?.[0];
			return {
				code: 'validation_failed',
				message: typeof first === 'string' && first.trim().length > 0 ? first : undefined,
				details: Object.keys(details).length > 0 ? details : undefined
			};
		}

		const detail = typeof obj.detail === 'string' ? obj.detail : undefined;
		const title = typeof obj.title === 'string' ? obj.title : undefined;
		if (detail || title) {
			return { code: 'request_failed', message: detail ?? title };
		}
	}

	if (typeof payload === 'string') {
		const text = payload.trim();
		if (!text) {
			return {};
		}

		// Attempt JSON parse.
		if ((text.startsWith('{') && text.endsWith('}')) || (text.startsWith('[') && text.endsWith(']'))) {
			try {
				return parseUnknownErrorPayload(JSON.parse(text));
			} catch {
				/* ignore */
			}
		}

		// HTML error pages or proxies.
		if (text.toLowerCase().includes('<html')) {
			return { code: 'request_failed', message: 'Request failed. Please try again.' };
		}

		return { code: 'request_failed', message: text.slice(0, 200) };
	}

	return {};
};

async function apiRequest<T>(path: string, config: AxiosRequestConfig = {}, requestKey?: string): Promise<ApiResult<T>> {
	const url = ensureLeadingSlash(path);
	const key = requestKey ?? `${(config.method ?? 'GET').toUpperCase()} ${url}`;
	startApiRequest(key);

	try {
		const response = await httpClient.request<ApiEnvelope<T>>({
			url,
			...config
		});

		const envelope = response.data;

		if (envelope?.success) {
			finishApiRequest(key, null);
			return {
				ok: true,
				data: (envelope.data as T) ?? (undefined as unknown as T),
				meta: envelope.meta ?? null
			};
		}

		if (response.status >= 200 && response.status < 300 && !envelope) {
			finishApiRequest(key, null);
			return { ok: true, data: undefined as unknown as T };
		}

		const errorMessage = envelope?.error?.message || response.statusText || 'Request failed';
		finishApiRequest(key, errorMessage);
		return {
			ok: false,
			error: formatApiErrorMessage(errorMessage, response.status, envelope?.error?.code, envelope?.meta as ApiErrorMeta | null | undefined),
			status: response.status,
			code: envelope?.error?.code,
			details: envelope?.error?.details
		};
	} catch (error: unknown) {
		if (axios.isAxiosError(error)) {
			const envelope = error.response?.data as ApiEnvelope<T> | undefined;
			const rawPayload = error.response?.data;
			const status = error.response?.status;
			const fallbackMessage = error.message || 'Request failed';
			const unknown = parseUnknownErrorPayload(rawPayload);
			const message = envelope?.error?.message || unknown.message || fallbackMessage;
			const code = envelope?.error?.code || unknown.code;
			const details = envelope?.error?.details || unknown.details;
			const normalizedMessage = formatApiErrorMessage(
				message,
				status,
				code,
				(envelope?.meta ?? (rawPayload as { meta?: ApiErrorMeta | null } | undefined)?.meta) as ApiErrorMeta | null | undefined
			);
			finishApiRequest(key, message);
			return {
				ok: false,
				error: normalizedMessage,
				status,
				code,
				details
			};
		}

		const message = error instanceof Error ? error.message : 'Unable to reach the server.';
		finishApiRequest(key, message);
		return { ok: false, error: message };
	}
}

export interface LoginRequestDto {
	email: string;
	password: string;
	rememberMe?: boolean;
}

export interface ForgotPasswordRequestDto {
	email: string;
}

export interface ResetPasswordRequestDto {
	email: string;
	token: string;
	newPassword: string;
}

export interface RegisterRequestDto {
	email: string;
	password: string;
	firstName: string;
	dateOfBirth: string;
	gender: string | null;
	sexuality: string | null;
	pronouns?: string | null;
	lookingFor: string;
	latitude: number;
	longitude: number;
	wantsTestUser?: boolean;
	locationLabel?: string | null;
	honeypot?: string | null;
	altchaToken?: string | null;
	marketingOptIn?: boolean;
	approvalEmailsOptIn?: boolean;
	connectionEmailsOptIn?: boolean;
	termsAccepted?: boolean;
	privacyPolicyAccepted?: boolean;
	preferredLanguage?: 'da' | 'en' | null;
}

export interface RegistrationDraftRequestDto extends RegisterRequestDto {
	draftId?: string | null;
	emailBypassed?: boolean;
	emailConfirmed?: boolean;
	isTestUser?: boolean;
	metadataJson?: string | null;
}

export interface RegistrationDraftCredentialsRequestDto {
	draftId: string;
	email: string;
	password: string;
}

export interface RegistrationCredentialsDraftRequestDto {
	email: string;
	password: string;
	isTestUser?: boolean;
	preferredLanguage?: 'da' | 'en' | null;
}

export interface RegistrationDraftRecoveryStartRequestDto {
	email: string;
	preferredLanguage?: 'da' | 'en' | null;
}

export interface RegistrationDraftRecoveryConfirmRequestDto {
	email: string;
	code: string;
	password: string;
	preferredLanguage?: 'da' | 'en' | null;
}

export interface RegistrationDraftResponseDto {
	draftId: string;
	emailConfirmed: boolean;
	status?: string | null;
}

export interface AuthenticatedUserDto {
	id: string;
	email: string;
	firstName: string;
	age: number;
	gender: string | null;
	sexuality: string | null;
	pronouns?: string | null;
	lookingFor: string;
	emailConfirmed: boolean;
	twoFactorEnabled: boolean;
	isActive: boolean;
	isPaused: boolean;
	isApproved?: boolean | null;
	isTestUser: boolean;
	isAdmin: boolean;
	createdAtUtc: string;
	updatedAtUtc: string;
}

export interface AuthTokensResponseDto {
	accessToken: string;
	expiresAtUtc: string | null;
	user: AuthenticatedUserDto;
}

export interface ProfileSocialHandlesDto {
	platform: string | null;
	handle: string | null;
	signal: string | null;
	secondaryPlatform: string | null;
	secondaryHandle: string | null;
}

export interface ProfilePhotoDto {
	id: string;
	url: string;
	isApproved: boolean;
	isPendingReview: boolean;
	reviewError?: string | null;
	uploadedAtUtc: string;
	displayOrder: number;
}

export interface VenueDto {
	address: string;
	latitude: number;
	longitude: number;
	placeId?: string | null;
}

export interface ConnectionPreferencesDto {
	minAge: number;
	maxAge: number;
	gender: string | null;
	sexuality: string | null;
	maxDistanceKm: number;
}

export interface ProfileRevisionSnapshotDto {
	firstName: string;
	age: number;
	gender: string | null;
	sexuality: string | null;
	pronouns?: string | null;
	eventTime?: string | null;
	profileLink?: string | null;
	lookingFor: string;
	aboutMe: string;
	location: string;
	venue?: VenueDto | null;
	social: ProfileSocialHandlesDto;
}

export interface ProfilePendingRevisionDto {
	proposedProfile: ProfileRevisionSnapshotDto;
	hasProfileChanges: boolean;
	photoAddIds: string[];
	photoRemoveIds: string[];
	requestedAtUtc: string;
}

export interface ProfileDetailsDto {
	id: string;
	email: string;
	firstName: string;
	profileType: string;
	eventDate?: string | null;
	eventTime?: string | null;
	profileLink?: string | null;
	age: number;
	gender: string | null;
	sexuality: string | null;
	pronouns?: string | null;
	lookingFor: string;
	aboutMe: string;
	location: string;
	venue?: VenueDto | null;
	isActive: boolean;
	isPaused: boolean;
	isApproved?: boolean | null;
	isTestUser: boolean;
	isDifficultTimeUser: boolean;
	marketingOptIn: boolean;
	approvalEmailsOptIn: boolean;
	connectionEmailsOptIn: boolean;
	isVisibleToOthers: boolean;
	hasSubscription: boolean;
	subscriptionStatus: string;
	subscriptionCurrentPeriodEndUtc?: string | null;
	createdAtUtc: string;
	updatedAtUtc: string;
	social: ProfileSocialHandlesDto;
	photos: ProfilePhotoDto[];
	preferences?: ConnectionPreferencesDto | null;
	pendingRevision?: ProfilePendingRevisionDto | null;
}

export async function login(request: LoginRequestDto): Promise<ApiResult<AuthTokensResponseDto>> {
	return apiRequest<AuthTokensResponseDto>(
		'auth/login',
		{
			method: 'POST',
			data: request,
			timeout: 60_000
		},
		'auth/login'
	);
}

export async function forgotPassword(request: ForgotPasswordRequestDto): Promise<ApiResult<ApiMessage>> {
	return apiRequest<ApiMessage>(
		'auth/forgot-password',
		{
			method: 'POST',
			data: request
		},
		'auth/forgot-password'
	);
}

export async function resetPassword(request: ResetPasswordRequestDto): Promise<ApiResult<ApiMessage>> {
	return apiRequest<ApiMessage>(
		'auth/reset-password',
		{
			method: 'POST',
			data: request
		},
		'auth/reset-password'
	);
}

export async function register(request: RegisterRequestDto): Promise<ApiResult<AuthTokensResponseDto>> {
	return apiRequest<AuthTokensResponseDto>(
		'auth/register',
		{
			method: 'POST',
			data: request
		},
		'auth/register'
	);
}

export async function upsertRegistrationDraft(
	request: RegistrationDraftRequestDto
): Promise<ApiResult<RegistrationDraftResponseDto>> {
	return apiRequest<RegistrationDraftResponseDto>(
		'auth/registration/draft',
		{
			method: 'POST',
			data: request,
			timeout: REGISTRATION_EMAIL_STEP_TIMEOUT_MS
		},
		'auth/registration/draft'
	);
}

export async function upsertRegistrationCredentialsDraft(
	request: RegistrationCredentialsDraftRequestDto
): Promise<ApiResult<RegistrationDraftResponseDto>> {
	return apiRequest<RegistrationDraftResponseDto>(
		'auth/registration/draft/credentials',
		{
			method: 'POST',
			data: request,
			timeout: REGISTRATION_EMAIL_STEP_TIMEOUT_MS
		},
		'auth/registration/draft/credentials'
	);
}

export async function verifyRegistrationDraftCredentials(
	request: RegistrationDraftCredentialsRequestDto
): Promise<ApiResult<RegistrationDraftResponseDto>> {
	return apiRequest<RegistrationDraftResponseDto>(
		'auth/registration/draft/verify-credentials',
		{
			method: 'POST',
			data: request,
			timeout: REGISTRATION_EMAIL_STEP_TIMEOUT_MS
		},
		'auth/registration/draft/verify-credentials'
	);
}

export async function restartRegistrationDraft(
	request: RegistrationDraftCredentialsRequestDto
): Promise<ApiResult<RegistrationDraftResponseDto>> {
	return apiRequest<RegistrationDraftResponseDto>(
		'auth/registration/draft/restart',
		{
			method: 'POST',
			data: request,
			timeout: REGISTRATION_EMAIL_STEP_TIMEOUT_MS
		},
		'auth/registration/draft/restart'
	);
}

export async function startRegistrationDraftRecovery(
	request: RegistrationDraftRecoveryStartRequestDto
): Promise<ApiResult<ApiMessage>> {
	return apiRequest<ApiMessage>(
		'auth/registration/draft/recovery/start',
		{
			method: 'POST',
			data: request,
			timeout: REGISTRATION_EMAIL_STEP_TIMEOUT_MS
		},
		'auth/registration/draft/recovery/start'
	);
}

export async function confirmRegistrationDraftRecovery(
	request: RegistrationDraftRecoveryConfirmRequestDto
): Promise<ApiResult<RegistrationDraftResponseDto>> {
	return apiRequest<RegistrationDraftResponseDto>(
		'auth/registration/draft/recovery/confirm',
		{
			method: 'POST',
			data: request,
			timeout: REGISTRATION_EMAIL_STEP_TIMEOUT_MS
		},
		'auth/registration/draft/recovery/confirm'
	);
}

export async function finalizeRegistrationDraft(draftId: string): Promise<ApiResult<AuthTokensResponseDto>> {
	return apiRequest<AuthTokensResponseDto>(
		'auth/registration/finalize',
		{
			method: 'POST',
			data: { draftId }
		},
		'auth/registration/finalize'
	);
}

export async function sendRegistrationEmailVerification(draftId: string, altchaToken?: string | null): Promise<ApiResult<ApiMessage>> {
	return apiRequest<ApiMessage>(
		'auth/registration/send-email-verification',
		{
			method: 'POST',
			data: altchaToken ? { draftId, altchaToken } : { draftId },
			timeout: REGISTRATION_EMAIL_STEP_TIMEOUT_MS
		},
		'auth/registration/send-email-verification'
	);
}

export async function confirmRegistrationEmailCode(draftId: string, code: string): Promise<ApiResult<ApiMessage>> {
	return apiRequest<ApiMessage>(
		'auth/registration/confirm-email-code',
		{
			method: 'POST',
			data: { draftId, code }
		},
		'auth/registration/confirm-email-code'
	);
}

export interface ReapplyContextResponseDto {
	email: string;
	firstName: string;
	profileType: string;
	eventDate?: string | null;
	eventTime?: string | null;
	profileLink?: string | null;
	dateOfBirth?: string | null;
	age: number;
	gender: string | null;
	sexuality: string | null;
	pronouns?: string | null;
	lookingFor: string;
	latitude: number;
	longitude: number;
	locationLabel?: string | null;
	wantsTestUser: boolean;
	aboutMe: string;
	isVisibleToOthers: boolean;
	social: ProfileSocialHandlesDto;
	photos: ReapplyPhotoDto[];
}

export interface ReapplyPhotoDto {
	photoId: string;
	filePath: string;
	isApproved: boolean;
	isPendingRemoval: boolean;
	uploadedAtUtc: string;
	validationError?: string | null;
}

export interface ReapplyPhotosResponseDto {
	photos: ReapplyPhotoDto[];
}

export async function getReapplyContext(email: string, password: string): Promise<ApiResult<ReapplyContextResponseDto>> {
	return apiRequest<ReapplyContextResponseDto>('auth/registration/reapply-context', {
		method: 'POST',
		data: { email, password }
	});
}

export async function withdrawApplication(email: string, password: string): Promise<ApiResult<ApiMessage>> {
	return apiRequest<ApiMessage>('auth/registration/withdraw', {
		method: 'POST',
		data: { email, password }
	});
}

export async function uploadReapplyPhotos(
	email: string,
	password: string,
	files: File[],
	isVisibleToOthers?: boolean
): Promise<ApiResult<ReapplyPhotosResponseDto>> {
	const formData = new FormData();
	formData.append('Email', email);
	formData.append('Password', password);
	if (typeof isVisibleToOthers === 'boolean') {
		formData.append('IsVisibleToOthers', String(isVisibleToOthers));
	}
	files.forEach((file) => formData.append('Photos', file));

	return apiRequest<ReapplyPhotosResponseDto>('auth/registration/photos', {
		method: 'POST',
		data: formData,
		headers: { 'Content-Type': 'multipart/form-data' },
		timeout: REGISTRATION_PHOTO_UPLOAD_TIMEOUT_MS
	});
}

export async function deleteReapplyPhoto(
	email: string,
	password: string,
	photoId: string
): Promise<ApiResult<ReapplyPhotosResponseDto>> {
	return apiRequest<ReapplyPhotosResponseDto>(`auth/registration/photos/${photoId}`, {
		method: 'DELETE',
		data: { email, password }
	});
}

export async function checkEmailAvailability(email: string): Promise<ApiResult<{ available: boolean }>> {
	const params = new URLSearchParams({ email });
	return apiRequest<{ available: boolean }>(`auth/email-available?${params.toString()}`, {
		method: 'GET'
	});
}

export async function refresh(): Promise<ApiResult<AuthTokensResponseDto>> {
	return apiRequest<AuthTokensResponseDto>('auth/refresh', { method: 'POST', timeout: 8_000 }, 'auth/refresh');
}

export async function revoke(accessToken?: string): Promise<ApiResult<void>> {
	return authorizedRequest<void>('auth/revoke', accessToken, { method: 'POST' });
}

export async function changePassword(
	accessToken: string | null | undefined,
	currentPassword: string,
	newPassword: string
): Promise<ApiResult<ApiMessage>> {
	return authorizedRequest<ApiMessage>('auth/change-password', accessToken, {
		method: 'POST',
		data: {
			currentPassword,
			newPassword
		}
	});
}

export async function changeEmail(
	accessToken: string | null | undefined,
	currentPassword: string,
	newEmail: string
): Promise<ApiResult<ApiMessage>> {
	return authorizedRequest<ApiMessage>('auth/change-email', accessToken, {
		method: 'POST',
		data: {
			currentPassword,
			newEmail
		}
	});
}

export async function getProfile(accessToken?: string): Promise<ApiResult<ProfileDetailsDto>> {
	return authorizedRequest<ProfileDetailsDto>('profile/me', accessToken, {
		method: 'GET'
	});
}

export interface UpdateProfileRequestDto {
	firstName?: string | null;
	age?: number | null;
	gender?: string | null;
	sexuality?: string | null;
	pronouns?: string | null;
	newEvent?: boolean | null;
	eventDate?: string | null;
	eventTime?: string | null;
	profileLink?: string | null;
	lookingFor?: string | null;
	aboutMe?: string | null;
	location?: string | null;
	venue?: VenueDto | null;
	socialHandles?: {
		platform?: string | null;
		handle?: string | null;
		signal?: string | null;
		secondaryPlatform?: string | null;
		secondaryHandle?: string | null;
	} | null;
	marketingOptIn?: boolean;
	approvalEmailsOptIn?: boolean;
	connectionEmailsOptIn?: boolean;
	isVisibleToOthers?: boolean | null;
}

export async function updatePreferredLanguage(
	accessToken: string | null | undefined,
	preferredLanguage: 'da' | 'en'
): Promise<ApiResult<ApiMessage>> {
	return authorizedRequest<ApiMessage>('profile/preferences/language', accessToken, {
		method: 'PUT',
		data: { preferredLanguage }
	});
}

export async function updateProfile(accessToken: string, payload: UpdateProfileRequestDto): Promise<ApiResult<ApiMessage>> {
	return authorizedRequest<ApiMessage>('profile', accessToken, {
		method: 'PUT',
		data: payload
	});
}

export async function submitProfileFeedback(
	accessToken: string | null | undefined,
	message: string
): Promise<ApiResult<ApiMessage>> {
	return authorizedRequest<ApiMessage>('profile/feedback', accessToken, {
		method: 'POST',
		data: { message }
	});
}

export interface SubmitSecurityIssueReportRequestDto {
	contactName?: string | null;
	contactEmail?: string | null;
	summary?: string | null;
	affectedAsset: string;
	details: string;
	reproductionSteps?: string | null;
	altchaToken?: string | null;
}

export interface SubmitSecurityIssueReportResponseDto {
	trackingCode: string;
	status: string;
}

export interface SubmitContactMessageRequestDto {
	contactName: string;
	contactEmail: string;
	message: string;
	altchaToken?: string | null;
}

export interface SubmitSupportRequestDto {
	contactName?: string | null;
	contactEmail: string;
	device?: string | null;
	errorCodeOrText?: string | null;
	problem: string;
	occurredAtLocal?: string | null;
	altchaToken?: string | null;
}

export async function submitSecurityIssueReport(
	payload: SubmitSecurityIssueReportRequestDto
): Promise<ApiResult<SubmitSecurityIssueReportResponseDto>> {
	return apiRequest<SubmitSecurityIssueReportResponseDto>('security/reports', {
		method: 'POST',
		data: payload
	});
}

export async function submitContactMessage(payload: SubmitContactMessageRequestDto): Promise<ApiResult<ApiMessage>> {
	return apiRequest<ApiMessage>('contact/messages', {
		method: 'POST',
		data: payload
	});
}

export async function submitSupportRequest(payload: SubmitSupportRequestDto): Promise<ApiResult<ApiMessage>> {
	return apiRequest<ApiMessage>('support/requests', {
		method: 'POST',
		data: payload
	});
}

export async function updateLocation(
	accessToken: string | null | undefined,
	latitude: number,
	longitude: number
): Promise<ApiResult<ApiMessage>> {
	return authorizedRequest<ApiMessage>('location', accessToken, {
		method: 'PUT',
		data: { latitude, longitude }
	});
}

export async function uploadProfilePhotos(accessToken: string, files: File[]): Promise<ApiResult<ApiMessage>> {
	const formData = new FormData();
	files.forEach((file) => formData.append('photos', file));

	return authorizedRequest<ApiMessage>('profile/photos', accessToken, {
		method: 'POST',
		data: formData,
		headers: { 'Content-Type': 'multipart/form-data' },
		timeout: PHOTO_UPLOAD_TIMEOUT_MS
	});
}

export async function warmUpProfilePhotoUpload(accessToken: string): Promise<ApiResult<ApiMessage>> {
	return authorizedRequest<ApiMessage>('profile/photos/warmup', accessToken, {
		method: 'POST',
		timeout: PHOTO_UPLOAD_WARMUP_TIMEOUT_MS
	});
}

export async function warmUpRegistrationPhotoUpload(): Promise<ApiResult<ApiMessage>> {
	return apiRequest<ApiMessage>(
		'auth/registration/photos/warmup',
		{
			method: 'POST',
			timeout: PHOTO_UPLOAD_WARMUP_TIMEOUT_MS
		},
		'auth/registration/photos/warmup'
	);
}

export function sendRegistrationPhotoClientEvent(payload: RegistrationPhotoClientEventRequestDto): void {
	if (typeof window === 'undefined') {
		return;
	}

	const url = `${API_BASE_URL}/auth/registration/photos/client-event`;
	const body = JSON.stringify(payload);

	if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
		try {
			const blob = new Blob([body], { type: 'application/json' });
			if (navigator.sendBeacon(url, blob)) {
				return;
			}
		} catch {
			// Fall back to fetch below.
		}
	}

	void fetch(url, {
		method: 'POST',
		credentials: 'include',
		keepalive: true,
		headers: {
			'Content-Type': 'application/json',
			'X-Api-Version': '1.0',
			'X-Requested-With': 'XMLHttpRequest'
		},
		body
	}).catch(() => undefined);
}

export async function deleteProfilePhoto(accessToken: string, photoId: string): Promise<ApiResult<ApiMessage>> {
	return authorizedRequest<ApiMessage>(`profile/photos/${photoId}`, accessToken, {
		method: 'DELETE'
	});
}

export async function reorderProfilePhotos(accessToken: string, photoIds: string[]): Promise<ApiResult<ApiMessage>> {
	return authorizedRequest<ApiMessage>('profile/photos/order', accessToken, {
		method: 'PUT',
		data: { photoIds }
	});
}

export async function deleteProfile(
	accessToken: string | null | undefined,
	hardDelete = false
): Promise<ApiResult<ApiMessage>> {
	const path = hardDelete ? 'profile?hardDelete=true' : 'profile';
	return authorizedRequest<ApiMessage>(path, accessToken, {
		method: 'DELETE'
	});
}

export type BlobResult =
	| { ok: true; data: Blob; fileName?: string | null; status?: number }
	| { ok: false; error: string; status?: number; code?: string };

async function requestDataExport(
	config: AxiosRequestConfig,
	authenticationError: string
): Promise<BlobResult> {
	try {
		const response = await httpClient.request<Blob>({
			...config,
			responseType: 'blob',
		});

		const disposition = response.headers['content-disposition'];
		const fileNameConnection = disposition?.match(/filename="?([^";]+)"?/i);
		const fileName = fileNameConnection?.[1] ?? 'enkel-data-export.zip';

		return { ok: true, data: response.data, fileName, status: response.status };
	} catch (error: unknown) {
		if (axios.isAxiosError(error)) {
			let message = 'Unable to download export.';
			const status = error.response?.status;

			if (error.response?.data instanceof Blob) {
				try {
					const text = await error.response.data.text();
					if (text) {
						try {
							const parsed = JSON.parse(text);
							message = parsed?.error?.message || parsed?.title || parsed?.detail || message;
						} catch {
							message = text.length > 0 ? text.slice(0, 200) : message;
						}
					}
				} catch {
					// Ignore parse errors and fall back to default message
				}
			} else if (typeof error.message === 'string') {
				message = error.message;
			}

			if (status === 401 || status === 404) {
				message = authenticationError;
			}

			return { ok: false, error: message, status, code: error.code };
		}

		const message = error instanceof Error ? error.message : 'Unable to download export.';
		return { ok: false, error: message };
	}
}

export async function downloadDataExport(accessToken?: string | null): Promise<BlobResult> {
	return requestDataExport(
		{
			url: 'privacy/export',
			method: 'GET',
			headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined
		},
		'Please sign in again to download your export.'
	);
}

export async function downloadApplicationDataExport(email: string, password: string): Promise<BlobResult> {
	return requestDataExport(
		{
			url: 'privacy/application-export',
			method: 'POST',
			data: { email, password }
		},
		'Could not authenticate the application.'
	);
}

export async function pauseProfile(
	accessToken: string | null | undefined
): Promise<ApiResult<ApiMessage>> {
	return authorizedRequest<ApiMessage>('profile/pause', accessToken, {
		method: 'POST'
	});
}

export async function resumeProfile(
	accessToken: string | null | undefined
): Promise<ApiResult<ApiMessage>> {
	return authorizedRequest<ApiMessage>('profile/resume', accessToken, {
		method: 'POST'
	});
}

export interface AdminPendingUsersQueryDto {
	page?: number;
	pageSize?: number;
	search?: string;
	gender?: string;
	minAge?: number;
	maxAge?: number;
	includeRejected?: boolean;
}

export interface AdminPendingUsersResponseDto {
	items: AdminPendingUserDto[];
	totalCount: number;
	page: number;
	pageSize: number;
}

export interface AdminPendingUserDto {
	reviewType: 'NewUser' | 'ProfileUpdate';
	userId: string;
	email: string;
	firstName: string;
	age: number;
	gender: string | null;
	sexuality: string | null;
	pronouns?: string | null;
	lookingFor: string;
	aboutMe: string;
	isVisibleToOthers: boolean;
	skippedDescription: boolean;
	skippedPhotos: boolean;
	marketingOptIn: boolean;
	approvalEmailsOptIn: boolean;
	connectionEmailsOptIn: boolean;
	isTestUser: boolean;
	createdAtUtc: string;
	requestedAtUtc: string;
	lastDecisionReason?: string | null;
	lastDecisionWasApproved?: boolean | null;
	photos: AdminPendingUserPhotoDto[];
	socialHandles: AdminSocialHandlesDto;
	currentProfile?: AdminProfileSnapshotDto | null;
	proposedProfile?: AdminProfileSnapshotDto | null;
	proposedPhotoAdds: AdminPendingUserPhotoDto[];
	proposedPhotoRemovals: AdminPendingUserPhotoDto[];
}

export interface AdminPendingUserPhotoDto {
	photoId: string;
	filePath: string;
	isApproved: boolean;
	uploadedAtUtc: string;
	validationError?: string | null;
}

export interface AdminSocialHandlesDto {
	platform: string | null;
	handle: string | null;
	signal: string | null;
	secondaryPlatform: string | null;
	secondaryHandle: string | null;
}

export interface AdminProfileSnapshotDto {
	isNewEvent?: boolean;
	firstName: string;
	age: number;
	gender: string | null;
	sexuality: string | null;
	pronouns?: string | null;
	profileType?: string | null;
	eventDate?: string | null;
	eventTime?: string | null;
	profileLink?: string | null;
	lookingFor: string;
	aboutMe: string;
	locationLabel: string;
	venue?: VenueDto | null;
	socialHandles: AdminSocialHandlesDto;
}

const authorizedRequest = async <T>(
	path: string,
	accessToken: string | null | undefined,
	config: AxiosRequestConfig = {}
): Promise<ApiResult<T>> => {
	const headers: Record<string, unknown> = { ...(config.headers ?? {}) };

	if (accessToken) {
		headers.Authorization = `Bearer ${accessToken}`;
	}

	const finalConfig: AxiosRequestConfig = { ...config };
	if (Object.keys(headers).length > 0) {
		finalConfig.headers = headers as AxiosRequestHeaders;
	}

	return apiRequest<T>(path, finalConfig);
};

export async function getPendingUsers(
	accessToken: string | null | undefined,
	query: AdminPendingUsersQueryDto = {}
): Promise<ApiResult<AdminPendingUsersResponseDto>> {
	const params = new URLSearchParams();
	if (query.page) params.set('page', query.page.toString());
	if (query.pageSize) params.set('pageSize', query.pageSize.toString());
	if (query.search) params.set('search', query.search);
	if (query.gender) params.set('gender', query.gender);
	if (typeof query.minAge === 'number') params.set('minAge', query.minAge.toString());
	if (typeof query.maxAge === 'number') params.set('maxAge', query.maxAge.toString());
	if (query.includeRejected) params.set('includeRejected', 'true');

	const path = params.size > 0 ? `admin/pending-users?${params.toString()}` : 'admin/pending-users';
	return authorizedRequest<AdminPendingUsersResponseDto>(path, accessToken, { method: 'GET' });
}

export async function approvePendingUser(
	accessToken: string | null | undefined,
	userId: string,
	reason?: string
): Promise<ApiResult<ApiMessage>> {
	return authorizedRequest<ApiMessage>(`admin/approve/${userId}`, accessToken, {
		method: 'POST',
		data: reason ? { reason } : {}
	});
}

export async function rejectPendingUser(
	accessToken: string | null | undefined,
	userId: string,
	reason?: string
): Promise<ApiResult<ApiMessage>> {
	return authorizedRequest<ApiMessage>(`admin/reject/${userId}`, accessToken, {
		method: 'POST',
		data: reason ? { reason } : {}
	});
}

export interface AdminUserManagementEntryDto {
	userId: string;
	email: string;
	firstName: string;
	profileType?: string | null;
	approvalStatus: string;
	isActive: boolean;
	isPaused: boolean;
	isAdmin: boolean;
	connectionCount: number;
	createdAtUtc: string;
	confirmationText: string;
}

export interface AdminUsersResponseDto {
	items: AdminUserManagementEntryDto[];
	totalCount: number;
	page: number;
	pageSize: number;
}

export interface AdminUsersQueryDto {
	page?: number;
	pageSize?: number;
	search?: string;
}

export async function getAdminUsers(
	accessToken: string | null | undefined,
	query: AdminUsersQueryDto = {}
): Promise<ApiResult<AdminUsersResponseDto>> {
	const params = new URLSearchParams();
	if (query.page) params.set('page', query.page.toString());
	if (query.pageSize) params.set('pageSize', query.pageSize.toString());
	if (query.search) params.set('search', query.search);

	const path = params.size > 0 ? `admin/users?${params.toString()}` : 'admin/users';
	return authorizedRequest<AdminUsersResponseDto>(path, accessToken, { method: 'GET' });
}

export async function deleteAdminUser(
	accessToken: string | null | undefined,
	userId: string,
	confirmationText: string
): Promise<ApiResult<ApiMessage>> {
	return authorizedRequest<ApiMessage>(`admin/users/${userId}`, accessToken, {
		method: 'DELETE',
		data: { confirmationText }
	});
}

export interface AdminHoneypotEntryDto {
	id: string;
	source: string;
	path: string;
	payload?: string | null;
	ipAddress?: string | null;
	userAgent?: string | null;
	occurredAtUtc: string;
	archivedAtUtc?: string | null;
}

export interface AdminHoneypotResponseDto {
	items: AdminHoneypotEntryDto[];
	totalCount: number;
	page: number;
	pageSize: number;
}

export interface AdminHoneypotQueryDto {
	page?: number;
	pageSize?: number;
	source?: string;
	path?: string;
	archivedOnly?: boolean;
}

export interface AdminFeedbackEntryDto {
	id: string;
	userId: string;
	email: string;
	firstName: string;
	message: string;
	createdAtUtc: string;
	archivedAtUtc?: string | null;
}

export interface AdminFeedbackResponseDto {
	items: AdminFeedbackEntryDto[];
	totalCount: number;
	page: number;
	pageSize: number;
}

export interface AdminFeedbackQueryDto {
	page?: number;
	pageSize?: number;
	search?: string;
	archivedOnly?: boolean;
}

export interface AdminContactMessageEntryDto {
	id: string;
	contactName: string;
	contactEmail: string;
	message: string;
	createdAtUtc: string;
}

export interface AdminContactMessageResponseDto {
	items: AdminContactMessageEntryDto[];
	totalCount: number;
	page: number;
	pageSize: number;
}

export interface AdminContactMessageQueryDto {
	page?: number;
	pageSize?: number;
	search?: string;
	archivedOnly?: boolean;
}

export interface AdminSupportRequestEntryDto {
	id: string;
	contactName?: string | null;
	contactEmail: string;
	device?: string | null;
	errorCodeOrText?: string | null;
	problem: string;
	occurredAtLocal?: string | null;
	createdAtUtc: string;
}

export interface AdminSupportRequestResponseDto {
	items: AdminSupportRequestEntryDto[];
	totalCount: number;
	page: number;
	pageSize: number;
}

export interface AdminSupportRequestQueryDto {
	page?: number;
	pageSize?: number;
	search?: string;
	archivedOnly?: boolean;
}

export interface AdminSecurityReportEntryDto {
	id: string;
	trackingCode: string;
	status: string;
	contactName?: string | null;
	contactEmail?: string | null;
	summary?: string | null;
	affectedAsset: string;
	details: string;
	reproductionSteps?: string | null;
	createdAtUtc: string;
	statusUpdatedAtUtc: string;
}

export interface AdminSecurityReportStatusUpdateDto {
	status: string;
}

export interface AdminSecurityReportResponseDto {
	items: AdminSecurityReportEntryDto[];
	totalCount: number;
	page: number;
	pageSize: number;
}

export interface AdminSecurityReportQueryDto {
	page?: number;
	pageSize?: number;
	search?: string;
	archivedOnly?: boolean;
}

export interface AdminRawMalwareScanResponseDto {
	fileName: string;
	contentType: string;
	sizeBytes: number;
	scannerEnabled: boolean;
	scanSucceeded: boolean;
	scanStatus: string;
	engineResult: string;
	errorMessage?: string | null;
}

export async function getHoneypotEvents(
	accessToken: string | null | undefined,
	query: AdminHoneypotQueryDto = {}
): Promise<ApiResult<AdminHoneypotResponseDto>> {
	const params = new URLSearchParams();
	if (query.page) params.set('page', query.page.toString());
	if (query.pageSize) params.set('pageSize', query.pageSize.toString());
	if (query.source) params.set('source', query.source);
	if (query.path) params.set('path', query.path);
	if (query.archivedOnly) params.set('archivedOnly', 'true');

	const path = params.size > 0 ? `admin/honeypot?${params.toString()}` : 'admin/honeypot';
	return authorizedRequest<AdminHoneypotResponseDto>(path, accessToken, { method: 'GET' });
}

export async function archiveAdminHoneypotEvent(
	accessToken: string | null | undefined,
	eventId: string
): Promise<ApiResult<ApiMessage>> {
	return authorizedRequest<ApiMessage>(`admin/honeypot/${eventId}/archive`, accessToken, {
		method: 'POST'
	});
}

export async function unarchiveAdminHoneypotEvent(
	accessToken: string | null | undefined,
	eventId: string
): Promise<ApiResult<ApiMessage>> {
	return authorizedRequest<ApiMessage>(`admin/honeypot/${eventId}/unarchive`, accessToken, {
		method: 'POST'
	});
}

export async function getAdminFeedback(
	accessToken: string | null | undefined,
	query: AdminFeedbackQueryDto = {}
): Promise<ApiResult<AdminFeedbackResponseDto>> {
	const params = new URLSearchParams();
	if (query.page) params.set('page', query.page.toString());
	if (query.pageSize) params.set('pageSize', query.pageSize.toString());
	if (query.search) params.set('search', query.search);
	if (query.archivedOnly) params.set('archivedOnly', 'true');

	const path = params.size > 0 ? `admin/feedback?${params.toString()}` : 'admin/feedback';
	return authorizedRequest<AdminFeedbackResponseDto>(path, accessToken, { method: 'GET' });
}

export async function archiveAdminFeedback(
	accessToken: string | null | undefined,
	feedbackId: string
): Promise<ApiResult<ApiMessage>> {
	return authorizedRequest<ApiMessage>(`admin/feedback/${feedbackId}/archive`, accessToken, {
		method: 'POST'
	});
}

export async function unarchiveAdminFeedback(
	accessToken: string | null | undefined,
	feedbackId: string
): Promise<ApiResult<ApiMessage>> {
	return authorizedRequest<ApiMessage>(`admin/feedback/${feedbackId}/unarchive`, accessToken, {
		method: 'POST'
	});
}

export async function getAdminContactMessages(
	accessToken: string | null | undefined,
	query: AdminContactMessageQueryDto = {}
): Promise<ApiResult<AdminContactMessageResponseDto>> {
	const params = new URLSearchParams();
	if (query.page) params.set('page', query.page.toString());
	if (query.pageSize) params.set('pageSize', query.pageSize.toString());
	if (query.search) params.set('search', query.search);
	if (query.archivedOnly) params.set('archivedOnly', 'true');

	const path = params.size > 0 ? `admin/contact-messages?${params.toString()}` : 'admin/contact-messages';
	return authorizedRequest<AdminContactMessageResponseDto>(path, accessToken, { method: 'GET' });
}

export async function archiveAdminContactMessage(
	accessToken: string | null | undefined,
	messageId: string
): Promise<ApiResult<ApiMessage>> {
	return authorizedRequest<ApiMessage>(`admin/contact-messages/${messageId}/archive`, accessToken, {
		method: 'POST'
	});
}

export async function unarchiveAdminContactMessage(
	accessToken: string | null | undefined,
	messageId: string
): Promise<ApiResult<ApiMessage>> {
	return authorizedRequest<ApiMessage>(`admin/contact-messages/${messageId}/unarchive`, accessToken, {
		method: 'POST'
	});
}

export async function getAdminSupportRequests(
	accessToken: string | null | undefined,
	query: AdminSupportRequestQueryDto = {}
): Promise<ApiResult<AdminSupportRequestResponseDto>> {
	const params = new URLSearchParams();
	if (query.page) params.set('page', query.page.toString());
	if (query.pageSize) params.set('pageSize', query.pageSize.toString());
	if (query.search) params.set('search', query.search);
	if (query.archivedOnly) params.set('archivedOnly', 'true');

	const path = params.size > 0 ? `admin/support-requests?${params.toString()}` : 'admin/support-requests';
	return authorizedRequest<AdminSupportRequestResponseDto>(path, accessToken, { method: 'GET' });
}

export async function archiveAdminSupportRequest(
	accessToken: string | null | undefined,
	requestId: string
): Promise<ApiResult<ApiMessage>> {
	return authorizedRequest<ApiMessage>(`admin/support-requests/${requestId}/archive`, accessToken, {
		method: 'POST'
	});
}

export async function unarchiveAdminSupportRequest(
	accessToken: string | null | undefined,
	requestId: string
): Promise<ApiResult<ApiMessage>> {
	return authorizedRequest<ApiMessage>(`admin/support-requests/${requestId}/unarchive`, accessToken, {
		method: 'POST'
	});
}

export async function getAdminSecurityReports(
	accessToken: string | null | undefined,
	query: AdminSecurityReportQueryDto = {}
): Promise<ApiResult<AdminSecurityReportResponseDto>> {
	const params = new URLSearchParams();
	if (query.page) params.set('page', query.page.toString());
	if (query.pageSize) params.set('pageSize', query.pageSize.toString());
	if (query.search) params.set('search', query.search);
	if (query.archivedOnly) params.set('archivedOnly', 'true');

	const path = params.size > 0 ? `admin/security-reports?${params.toString()}` : 'admin/security-reports';
	return authorizedRequest<AdminSecurityReportResponseDto>(path, accessToken, { method: 'GET' });
}

export async function archiveAdminSecurityReport(
	accessToken: string | null | undefined,
	reportId: string
): Promise<ApiResult<ApiMessage>> {
	return authorizedRequest<ApiMessage>(`admin/security-reports/${reportId}/archive`, accessToken, {
		method: 'POST'
	});
}

export async function unarchiveAdminSecurityReport(
	accessToken: string | null | undefined,
	reportId: string
): Promise<ApiResult<ApiMessage>> {
	return authorizedRequest<ApiMessage>(`admin/security-reports/${reportId}/unarchive`, accessToken, {
		method: 'POST'
	});
}

export async function updateAdminSecurityReportStatus(
	accessToken: string | null | undefined,
	reportId: string,
	payload: AdminSecurityReportStatusUpdateDto
): Promise<ApiResult<AdminSecurityReportEntryDto>> {
	return authorizedRequest<AdminSecurityReportEntryDto>(`admin/security-reports/${reportId}/status`, accessToken, {
		method: 'POST',
		data: payload
	});
}

export async function rawAdminMalwareScan(
	accessToken: string | null | undefined,
	file: File
): Promise<ApiResult<AdminRawMalwareScanResponseDto>> {
	const formData = new FormData();
	formData.append('file', file);

	return authorizedRequest<AdminRawMalwareScanResponseDto>('admin/security/raw-malware-scan', accessToken, {
		method: 'POST',
		data: formData,
		headers: {
			'Content-Type': 'multipart/form-data'
		}
	});
}

export interface ConnectionCandidateDto {
	userId: string;
	firstName: string;
	profileType?: string | null;
	eventDate?: string | null;
	eventTime?: string | null;
	venue?: VenueDto | null;
	age: number;
	gender: string | null;
	sexuality: string | null;
	pronouns?: string | null;
	lookingFor?: string;
	about?: string;
	distanceKm: number;
}

export interface ConnectionCandidatesResponseDto {
	count: number;
	candidates: ConnectionCandidateDto[];
}

export interface ConnectionResultDto {
	isMutualConnection: boolean;
	matchId: string | null;
}

export interface ConnectionSocialHandlesDto {
	platform: string | null;
	handle: string | null;
	signal: string | null;
	secondaryPlatform: string | null;
	secondaryHandle: string | null;
}

export interface ConnectionListItemDto {
	connectionId?: string;
	matchId?: string;
	partnerId: string;
	firstName: string;
	profileType?: string | null;
	eventDate?: string | null;
	eventTime?: string | null;
	profileLink?: string | null;
	venue?: VenueDto | null;
	age: number;
	about: string;
	distanceKm?: number | null;
	photos: string[];
	socialHandles: ConnectionSocialHandlesDto;
	connectedAtUtc?: string;
	matchedAtUtc?: string;
	isRecent: boolean;
	isInactive: boolean;
	gender?: string;
	sexuality?: string;
	pronouns?: string | null;
	lookingFor?: string;
}

export interface ConnectionListResponseDto {
	connections: ConnectionListItemDto[];
}

export interface ConnectQueueSocialHandlesDto {
	isHidden: boolean;
	platform?: string | null;
	secondaryPlatform?: string | null;
}

export interface ConnectQueueProfileDto {
	userId: string;
	firstName: string;
	profileType?: string | null;
	eventDate?: string | null;
	eventTime?: string | null;
	profileLink?: string | null;
	venue?: VenueDto | null;
	age: number;
	gender: string | null;
	sexuality: string | null;
	pronouns?: string | null;
	lookingFor?: string;
	about: string;
	distanceKm: number;
	photos: string[];
	socialHandles: ConnectQueueSocialHandlesDto;
}

export interface ConnectQueueResponseDto {
	count: number;
	profiles: ConnectQueueProfileDto[];
}

export interface ConnectRequestDto {
	candidateId: string;
	isLike: boolean;
}

export interface ConnectLimitStatusDto {
	remaining: number;
	max: number;
	resetsAtUtc: string;
	resetsAtLocal: string;
}

export interface BlockUserRequestDto {
	targetUserId: string;
	reason?: string;
}

export interface ReportUserRequestDto {
	targetUserId: string;
	reason: string;
	category: string;
}

export interface UserFiltersDto {
	minAge: number;
	maxAge: number;
}

export interface UpdateUserFiltersRequest {
	minAge: number;
	maxAge: number;
}

export interface CheckoutSessionResponseDto {
	checkoutUrl: string;
	sessionId: string;
}

export interface PortalSessionResponseDto {
	portalUrl: string;
}

export interface CancelSubscriptionRequestDto {
	reason?: string | null;
}

export async function getConnectionCandidates(
	limit = 20,
	accessToken?: string | null
): Promise<ApiResult<ConnectionCandidatesResponseDto>> {
	const params = new URLSearchParams();
	if (limit) {
		params.set('limit', limit.toString());
	}
	const path = params.size > 0 ? `connections/candidates?${params.toString()}` : 'connections/candidates';
	return authorizedRequest<ConnectionCandidatesResponseDto>(path, accessToken, { method: 'GET' });
}

export async function getConnectiones(accessToken?: string | null): Promise<ApiResult<ConnectionListResponseDto>> {
	return authorizedRequest<ConnectionListResponseDto>('connections', accessToken, {
		method: 'GET'
	});
}

export async function getConnectQueue(
	count = 10,
	accessToken?: string | null
): Promise<ApiResult<ConnectQueueResponseDto>> {
	const params = new URLSearchParams();
	if (count) {
		params.set('count', count.toString());
	}
	const path = params.size > 0 ? `connect/queue?${params.toString()}` : 'connect/queue';
	return authorizedRequest<ConnectQueueResponseDto>(path, accessToken, { method: 'GET' });
}

export async function getConnectLimitStatus(accessToken?: string | null): Promise<ApiResult<ConnectLimitStatusDto>> {
	return authorizedRequest<ConnectLimitStatusDto>('connect/limit', accessToken, {
		method: 'GET'
	});
}

export async function submitConnectAction(
	accessToken: string | null | undefined,
	payload: ConnectRequestDto
): Promise<ApiResult<ConnectionResultDto>> {
	return authorizedRequest<ConnectionResultDto>('connect/action', accessToken, {
		method: 'POST',
		data: payload
	});
}

export async function blockUser(
	accessToken: string | null | undefined,
	payload: BlockUserRequestDto
): Promise<ApiResult<void>> {
	return authorizedRequest<void>('connections/block', accessToken, {
		method: 'POST',
		data: payload
	});
}

export async function reportUser(
	accessToken: string | null | undefined,
	payload: ReportUserRequestDto
): Promise<ApiResult<void>> {
	return authorizedRequest<void>('connections/report', accessToken, {
		method: 'POST',
		data: payload
	});
}

export async function deleteConnection(
	matchId: string,
	accessToken?: string | null
): Promise<ApiResult<ApiMessage>> {
	return authorizedRequest<ApiMessage>(`connections/${matchId}`, accessToken, {
		method: 'DELETE'
	});
}

export async function getUserFilters(accessToken?: string | null): Promise<ApiResult<UserFiltersDto>> {
	return authorizedRequest<UserFiltersDto>('filters', accessToken, {
		method: 'GET'
	});
}

export async function updateUserFilters(
	accessToken: string | null | undefined,
	payload: UpdateUserFiltersRequest
): Promise<ApiResult<UserFiltersDto>> {
	return authorizedRequest<UserFiltersDto>('filters', accessToken, {
		method: 'PUT',
		data: payload
	});
}

export async function createCheckoutSession(
	accessToken: string | null | undefined,
	successUrl: string,
	cancelUrl: string
): Promise<ApiResult<CheckoutSessionResponseDto>> {
	return authorizedRequest<CheckoutSessionResponseDto>('payments/checkout', accessToken, {
		method: 'POST',
		data: { successUrl, cancelUrl }
	});
}

export async function createPortalSession(
	accessToken: string | null | undefined,
	returnUrl: string
): Promise<ApiResult<PortalSessionResponseDto>> {
	return authorizedRequest<PortalSessionResponseDto>('payments/portal', accessToken, {
		method: 'POST',
		data: { returnUrl }
	});
}

export async function undoCancelSubscription(accessToken: string | null | undefined): Promise<ApiResult<ApiMessage>> {
	return authorizedRequest<ApiMessage>('payments/undo-cancel', accessToken, { method: 'POST' });
}

export async function cancelSubscription(
	accessToken: string | null | undefined,
	payload: CancelSubscriptionRequestDto = {}
): Promise<ApiResult<ApiMessage>> {
	return authorizedRequest<ApiMessage>('payments/cancel', accessToken, {
		method: 'POST',
		data: payload
	});
}
